import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BullhornNamespace, InveniasNamespace, SalesforceNamespace } from 'datasource-service-lib';
import { INamespace } from 'interfaces-lib';
import { from, Observable, of } from 'rxjs';
import { concatMap, tap } from 'rxjs/operators';
import { runtimeEnvironment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class RuntimeEnvironmentService {
  constructor(private http: HttpClient) {}

  protected getLocationProtocol(): string {
    return window.location.protocol;
  }

  protected getLocationHref(): string {
    return window.location.href;
  }

  initialise(): Observable<void> {
    if (this.getLocationProtocol() === 'chrome-extension:') {
      return this.initialiseChromeExtensionRuntimeEnvironment();
    }

    return this.initialiseWebAppRuntimeEnvironment();
  }

  private initialiseChromeExtensionRuntimeEnvironment(): Observable<void> {
    runtimeEnvironment.runningAsChromeExtension = true;
    runtimeEnvironment.renderMode = 'chrome-extension';

    const getTabs$ = from(chrome.tabs.query({ active: true, currentWindow: true }));
    return getTabs$.pipe(
      tap((tabs) => (runtimeEnvironment.origin = this.getOrigin(tabs[0].url))),
      tap((tabs) => (runtimeEnvironment.client.name = this.getClientName(runtimeEnvironment.origin.namespace, tabs[0].url))),
      concatMap((_) => of(null))
    );
  }

  private initialiseWebAppRuntimeEnvironment(): Observable<void> {
    runtimeEnvironment.runningAsChromeExtension = false;
    runtimeEnvironment.renderMode = 'web-app';
    runtimeEnvironment.origin = this.getOrigin(this.getLocationHref());
    runtimeEnvironment.client.name = this.getClientName(runtimeEnvironment.origin.namespace, this.getLocationHref());
    return of(null);
  }

  private getOrigin(url: string): { url: string; namespace: string } {
    const namespace = this.getNamespace(url);
    return { namespace: namespace.name, url: url };
  }

  private getNamespace(url: string): INamespace {
    const namespaceRegistry: INamespace[] = [InveniasNamespace, BullhornNamespace, SalesforceNamespace];
    const nsLookup = (value: string) => {
      return namespaceRegistry.find((nsi) => nsi.name === value || nsi.aliases.includes(value));
    };

    const u = new URL(url);
    let namespace;
    if (runtimeEnvironment.runningAsChromeExtension) {
      // host examples: kyloedev.invenias.com or kyloe.inveniasnextgen.com
      const hostParts = u.hostname.split('.');
      const hostDomainName = hostParts[hostParts.length - 2];
      namespace = nsLookup(hostDomainName);
    } else {
      // running as web-app (e.g. https://adv2-ui-uat.kyloe.plus/invenias-mock/kyloedev)
      const path = u.pathname.split('/').filter((p) => p.length > 0);
      if (path.length === 0) {
        throw new Error('Namespace missing.');
      }

      namespace = nsLookup(path[0]);
    }

    if (!namespace) {
      throw new Error(`Namespace not found.`);
    }

    return namespace;
  }

  private getClientName(namespace: string, url: string): string | undefined {
    const u = new URL(url);

    if (namespace === InveniasNamespace.name) {
      if (runtimeEnvironment.runningAsChromeExtension) {
        // host examples: kyloedev.invenias.com or kyloe.inveniasnextgen.com
        return u.hostname.split('.')[0];
      }

      // running as web-app (e.g. https://adv2-ui-uat.kyloe.plus/invenias-mock/kyloedev)
      const path = u.pathname.split('/').filter((p) => p.length > 0);
      if (path.length < 2) {
        throw new Error('Client name missing.');
      }

      return path[1];
    }

    // TODO: BW - remove this - the client name / corporation ID will come from the JWT
    // if (namespace === BullhornNamespace.name) {
    //   let clientName = u.searchParams.get('CorporationID');
    //   if (!clientName) {
    //     const path = u.pathname.split('/').filter(p => p.length > 0);
    //     if (path.length < 2)
    //       console.warn('Corporation ID missing from path. Should we check after login?');
    //     else
    //       clientName = path[1];
    //   }
    //   return clientName;
    // }

    return undefined;
  }
}
